import React, { useCallback, useEffect, useState } from "react";
import { ArrowRightCircle, ChevronLeft, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { FIXTURE_SCOPES, SEASON_PHASE_META } from "../constants";
import { formatDate, formatRelative } from "../helpers";
import {
  fetchAdminCandidateFixtures,
  promoteAdminLeagueCandidate,
  syncAdminCandidateFixtures,
  syncAdminFixture,
} from "../api/fetchAdminLeagues";
import { FixtureRow, FixturesListHeader } from "./FixturesList";
import LinkedActivityPanel from "./LinkedActivityPanel";

export default function LeagueCandidateDetail({ candidate, onPromoted, onUpdated }) {
  const [promoting, setPromoting] = useState(false);
  const [scope, setScope] = useState("upcoming");
  const [fixtures, setFixtures] = useState([]);
  const [fxPagination, setFxPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });
  const [fxPage, setFxPage] = useState(1);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxSyncingId, setFxSyncingId] = useState(null);
  const [fixtureWindow, setFixtureWindow] = useState("close");
  const [fixtureWindowSyncing, setFixtureWindowSyncing] = useState(false);
  const [linkedActivity, setLinkedActivity] = useState(null);

  useEffect(() => {
    setScope("upcoming");
    setFxPage(1);
    setLinkedActivity(null);
  }, [candidate?._id]);

  const loadFixtures = useCallback(
    async (pageNum = 1) => {
      if (!candidate?._id) return;
      setFxLoading(true);
      try {
        const data = await fetchAdminCandidateFixtures(candidate._id, {
          page: pageNum,
          limit: 20,
          scope,
        });
        setFixtures(data?.fixtures || []);
        setFxPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
        }));
        setLinkedActivity(data?.linkedActivity || null);
        if (data?.candidate) {
          onUpdated?.(data.candidate);
        }
      } catch (err) {
        setFixtures([]);
        toast.error(err.response?.data?.error || "Failed to load fixtures");
      } finally {
        setFxLoading(false);
      }
    },
    [candidate?._id, scope]
  );

  useEffect(() => {
    if (!candidate?._id) return;
    loadFixtures(fxPage);
  }, [candidate?._id, scope, fxPage, loadFixtures]);

  useEffect(() => {
    setFxPage(1);
  }, [scope, candidate?._id]);

  if (!candidate) {
    return (
      <section className="amf-detail amf-detail-idle alg-detail" aria-label="Select a candidate league">
        <div className="amf-idle">
          <p className="amf-idle-eyebrow">Leagues desk</p>
          <p className="amf-idle-title">Select a candidate from the pool</p>
          <p className="amf-idle-copy">Sync staging fixtures, then promote to managed leagues.</p>
        </div>
      </section>
    );
  }

  const phaseMeta = SEASON_PHASE_META[candidate.seasonPhase] || SEASON_PHASE_META.unknown;

  const handlePromote = async () => {
    setPromoting(true);
    try {
      const data = await promoteAdminLeagueCandidate(candidate._id, { isEnabled: true });
      const message = data.alreadyManaged
        ? `${data.league?.name || candidate.name} is already managed — removed from pool`
        : `${data.league?.name || candidate.name} added to managed leagues`;
      toast.success(message);
      onPromoted(data.league, candidate._id);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to promote candidate");
    } finally {
      setPromoting(false);
    }
  };

  const handleFixturesWindowSync = async () => {
    setFixtureWindowSyncing(true);
    try {
      const data = await syncAdminCandidateFixtures(candidate._id, { window: fixtureWindow });
      const range = data.dateRange
        ? ` (${data.dateRange.startDate} → ${data.dateRange.endDate})`
        : "";
      toast.success(
        `Synced ${data.processedCount ?? 0}/${data.totalCount ?? 0} fixture(s)${range}`
      );
      if (data.errorCount) {
        toast.warn(`${data.errorCount} fixture sync error(s). Check backend logs for details.`);
      }
      if (data.settlementNote) {
        toast.info(data.settlementNote, { autoClose: 7000 });
      }
      if (data.candidate) {
        onUpdated?.(data.candidate);
      }
      setFxPage(1);
      await loadFixtures(1);
    } catch (err) {
      toast.error(err.response?.data?.error || "Fixture sync failed");
    } finally {
      setFixtureWindowSyncing(false);
    }
  };

  const handleFixtureSync = async (fixture) => {
    if (!fixture?._id) return;
    setFxSyncingId(fixture._id);
    try {
      const data = await syncAdminFixture(fixture._id);
      setFixtures((rows) =>
        rows.map((row) => (row._id === fixture._id ? data.fixture : row))
      );

      let message = "Fixture updated from API-Sports";
      if (data.changes?.statusChanged) {
        message += ` · ${data.changes.previousStatus} → ${data.changes.newStatus}`;
      }
      toast.success(message);
      if (data.settlementNote) {
        toast.info(data.settlementNote, { autoClose: 7000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Fixture sync failed");
    } finally {
      setFxSyncingId(null);
    }
  };

  return (
    <section className="amf-detail alg-detail" aria-label="Candidate detail">
      <header className="amf-desk amf-desk-with-question alg-desk-header">
        <div className="alg-desk-identity">
          <span className="alg-desk-logo">
            {candidate.logo ? (
              <img src={candidate.logo} alt="" />
            ) : (
              <span className="alg-row-logo-fallback">{candidate.name?.[0] || "?"}</span>
            )}
          </span>
          <div className="alg-desk-copy">
            <p className="amf-desk-eyebrow">
              {candidate.country?.name || "—"} · API {candidate.apiId}
            </p>
            <h2 className="amf-desk-title alg-desk-title">{candidate.name}</h2>
          </div>
        </div>

        <div className="alg-desk-trail">
          <div className="amf-desk-stats alg-desk-stats">
            <DetailStat label="Fixtures" value={candidate.fixtureCount ?? 0} />
            <DetailStat label="Season" value={candidate.currentSeasonYear ?? "—"} />
            <DetailStat label="Type" value={candidate.type || "—"} />
          </div>
          <div className="alg-desk-controls">
            <button
              type="button"
              className="alg-btn alg-btn-accent"
              onClick={handlePromote}
              disabled={promoting || fixtureWindowSyncing}
              title="Add to managed leagues and enable fixture sync"
            >
              {promoting ? (
                <Loader2 size={12} className="am-spin" aria-hidden />
              ) : (
                <ArrowRightCircle size={12} aria-hidden />
              )}
              Add to leagues
            </button>
          </div>
        </div>
      </header>

      <div className="amf-detail-scroll alg-detail-scroll">
        <div className="alg-detail-timing">
          <div className="alg-detail-time">
            <span className="alg-detail-time-key">Phase</span>
            <span className="alg-detail-time-val">{phaseMeta.label}</span>
          </div>
          <div className="alg-detail-time">
            <span className="alg-detail-time-key">Season window</span>
            <span className="alg-detail-time-val">
              {formatDate(candidate.currentSeasonStartDate)} →{" "}
              {formatDate(candidate.currentSeasonEndDate)}
            </span>
          </div>
          <div className="alg-detail-time">
            <span className="alg-detail-time-key">Discovered</span>
            <span className="alg-detail-time-val">{formatRelative(candidate.discoveredAt)}</span>
          </div>
        </div>

        {candidate.linkedLeagueId ? (
          <LinkedActivityPanel activity={linkedActivity} compact />
        ) : null}

        <div className="alg-fixtures-panel">
        <div className="alg-fixtures-toolbar">
          <div className="alg-scope-switch">
            {FIXTURE_SCOPES.map((item) => (
              <button
                key={item.id}
                type="button"
                className={scope === item.id ? "is-on" : ""}
                onClick={() => setScope(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <span className="alg-fixtures-count tabular-nums">
            {fxPagination.total ?? 0} in DB
          </span>
          <div className="alg-fixtures-sync-controls">
            <select
              className="alg-fixture-window-select"
              value={fixtureWindow}
              onChange={(e) => setFixtureWindow(e.target.value)}
              disabled={fixtureWindowSyncing}
              aria-label="Fixture sync window"
            >
              <option value="close">Close window · -7d to +7d</option>
              <option value="long">Long window · today to +30d</option>
            </select>
            <button
              type="button"
              className="alg-fixture-window-sync"
              onClick={handleFixturesWindowSync}
              disabled={fixtureWindowSyncing || fxLoading}
              title="Pull fixtures for this candidate using the selected app sync window"
            >
              {fixtureWindowSyncing ? (
                <Loader2 size={12} className="am-spin" aria-hidden />
              ) : (
                <RefreshCw size={12} aria-hidden />
              )}
              Sync fixtures
            </button>
          </div>
        </div>

        <p className="alg-fixtures-hint">
          Staging fixtures only — jobs and settlement do not run until you promote this candidate.
        </p>

        {fixtures.length > 0 ? <FixturesListHeader /> : null}

        <div className={`alg-fixtures-list${fxLoading ? " is-loading" : ""}`}>
          {fxLoading && fixtures.length === 0 ? (
            <div className="alg-index-loading">
              <Loader2 size={18} className="am-spin" />
              <span>Loading fixtures…</span>
            </div>
          ) : fixtures.length === 0 ? (
            <div className="amf-index-empty">
              No {scope === "all" ? "" : `${scope} `}fixtures in the database for this candidate.
              {" "}Run Sync fixtures above to pull from API-Sports.
            </div>
          ) : (
            fixtures.map((fx) => (
              <FixtureRow
                key={fx._id}
                fixture={fx}
                syncing={fxSyncingId === fx._id}
                onSync={handleFixtureSync}
              />
            ))
          )}
        </div>

        {fxPagination.totalPages > 1 ? (
          <div className="amf-index-foot alg-fixtures-foot">
            <button
              type="button"
              className="amf-index-page"
              disabled={!fxPagination.hasPrev || fxLoading}
              onClick={() => setFxPage((p) => p - 1)}
            >
              <ChevronLeft size={14} />
            </button>
            <span className="amf-index-page-label">
              {fxPagination.page} / {fxPagination.totalPages}
            </span>
            <button
              type="button"
              className="amf-index-page"
              disabled={!fxPagination.hasNext || fxLoading}
              onClick={() => setFxPage((p) => p + 1)}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        ) : null}
        </div>
      </div>
    </section>
  );
}

function DetailStat({ label, value }) {
  return (
    <div className="amf-desk-stat">
      <span className="amf-desk-stat-val">{value}</span>
      <span className="amf-desk-stat-key">{label}</span>
    </div>
  );
}
